import { CSSProperties, ReactNode, useRef, useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DEFAULT_TYPE, DragItem, DragPosition } from './types';
import { StyledListItem } from './styles';

type DraggableListProps<T> = {
    items: T[];
    onReorder: (items: T[]) => void;
    renderItem: (item: T, index: number) => ReactNode;
    getKey?: (item: T, index: number) => string;
    type?: string;
    className?: string;
    listStyle?: CSSProperties;
    itemStyle?: CSSProperties;
    useProvider?: boolean;
};

type DraggableListItemProps<T> = {
    item: T;
    index: number;
    moveItem: (from: number, to: number) => void;
    renderItem: (item: T, index: number) => ReactNode;
    type: string;
    style?: CSSProperties;
};

function BaseDraggableList<T>({
    items,
    onReorder,
    renderItem,
    getKey,
    type = DEFAULT_TYPE,
    className,
    listStyle,
    itemStyle,
}: DraggableListProps<T>) {
    const moveItem = (from: number, to: number) => {
        if (from === to) return;

        const updated = [...items];
        const [moved] = updated.splice(from, 1);
        updated.splice(to, 0, moved);

        onReorder(updated);
    };

    return (
        <ul
            className={className}
            style={{
                padding: 0,
                margin: 0,
                ...listStyle,
            }}
        >
            {items.map((item, index) => (
                <DraggableListItem
                    key={getKey ? getKey(item, index) : `${index}`}
                    item={item}
                    index={index}
                    moveItem={moveItem}
                    renderItem={renderItem}
                    type={type}
                    style={itemStyle}
                />
            ))}
        </ul>
    );
}

function DraggableListItem<T>({ item, index, moveItem, renderItem, type, style }: DraggableListItemProps<T>) {
    const ref = useRef<HTMLLIElement>(null);
    const [dropPosition, setDropPosition] = useState<DragPosition>(null);

    const [{ isDragging }, drag] = useDrag({
        type,
        item: { index },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const [, drop] = useDrop<DragItem>({
        accept: type,
        hover(draggedItem, monitor) {
            if (!ref.current) return;

            if (draggedItem.index === index) {
                setDropPosition(null);
                return;
            }

            const rect = ref.current.getBoundingClientRect();
            const clientOffset = monitor.getClientOffset();
            if (!clientOffset) return;

            const middleY = (rect.bottom - rect.top) / 2;
            const hoverClientY = clientOffset.y - rect.top;

            setDropPosition(hoverClientY < middleY ? 'top' : 'bottom');
        },
        drop(draggedItem, monitor) {
            if (!ref.current || !dropPosition) return;
            if (monitor.didDrop()) return;

            let targetIndex = index;

            if (dropPosition === 'bottom') {
                targetIndex = index + 1;
            }

            if (draggedItem.index < targetIndex) {
                targetIndex -= 1;
            }

            if (draggedItem.index !== targetIndex) {
                moveItem(draggedItem.index, targetIndex);
            }

            setDropPosition(null);
        },
        collect: (monitor) => {
            if (!monitor.isOver({ shallow: true }) && dropPosition !== null) {
                setDropPosition(null);
            }
            return {};
        },
    });

    drop(ref);
    drag(ref);

    return (
        <StyledListItem ref={ref} $isDragging={isDragging} $dropPosition={dropPosition} style={style}>
            {renderItem(item, index)}
        </StyledListItem>
    );
}

export default function DraggableList<T>(props: DraggableListProps<T>) {
    const { useProvider = true } = props;

    if (!useProvider) {
        return <BaseDraggableList {...props} />;
    }

    return (
        <DndProvider backend={HTML5Backend}>
            <BaseDraggableList {...props} />
        </DndProvider>
    );
}
